import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Fade, CircularProgress } from '@mui/material';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Stack,
  Alert,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AutoStories,
  QuestionAnswer,
  AccountTree,
  RecordVoiceOver,
  School,
  ArrowForward,
  Description,
  Grain
} from '@mui/icons-material';
import { generateContent } from '../utils/ai';
import ExamMode from './ExamMode';

const features = [
  {
    icon: <AutoStories fontSize="large" />,
    title: 'AI Notes Generator',
    description: 'Summarizes PDFs, textbooks, and lectures with AI precision.'
  },
  {
    icon: <QuestionAnswer fontSize="large" />,
    title: 'Smart Flashcards',
    description: 'Auto-creates Q&A pairs for effective revision.'
  },
  {
    icon: <AccountTree fontSize="large" />,
    title: 'Mind Maps',
    description: 'Converts complex topics into visual diagrams.'
  },
  {
    icon: <Grain fontSize="large" />,
    title: 'Diagram Generator',
    description: 'Creates flowcharts and visual aids from text descriptions.'
  },
  {
    icon: <RecordVoiceOver fontSize="large" />,
    title: 'Voice-to-Notes',
    description: 'Converts lecture audio into key points.'
  },
  {
    icon: <School fontSize="large" />,
    title: 'Exam Mode',
    description: 'AI-generated practice questions for better preparation.'
  },
  {
    icon: <Description fontSize="large" />,
    title: 'Sample Paper',
    description: 'Access AI-generated sample papers for practice.'
  },
  {
    icon: <School fontSize="large" />,
    title: 'Schedule Maker',
    description: 'Create optimized study schedules.'
  }
];

function Home() {
  // State for LICENSE AND MORE dropdown
  const [licenseMenuAnchor, setLicenseMenuAnchor] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  useEffect(() => {
    async function checkAuth() {
      try {
        const user = await (auth.getCurrentUser?.() ?? null);
        setIsSignedIn(!!user);
      } catch (e) {
        setIsSignedIn(false);
      }
    }
    checkAuth();
  }, []);

  const navigate = useNavigate();

  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState('');

  useEffect(() => {
    // Simulate content loading
    const timer = setTimeout(() => setLoading(false), 1000);

    // Check server status
    const checkServerStatus = async () => {
      try {
        const messages = [{ role: 'user', content: 'Test' }];
        await generateContent(messages, { max_tokens: 1, temperature: 0.7 });
        setServerStatus('Server Online');
      } catch (error) {
        console.error('Error checking server status:', error);
        setServerStatus('Unable to connect to server. Please try again later.');
      }
    };

    checkServerStatus();

    return () => clearTimeout(timer);
  }, []);

  const gradientStyle = {
    background: 'linear-gradient(135deg, #6B46C1 0%, #3182CE 100%)',
    color: 'white',
    borderRadius: '0 0 20% 20%',
    position: 'relative',
    overflow: 'hidden',
    py: { xs: 8, md: 12 },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width=\'100%\' height=\'100%\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23ffffff10\' fill-opacity=\'0.1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
      opacity: 0.1,
      animation: 'float 20s linear infinite'
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Fade in={visible} timeout={1000}>
        <Box sx={{ flex: 1 }}>
          {serverStatus.includes('down') && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverStatus}
            </Alert>
          )}

          {/* Hero Section */}
          <Box sx={gradientStyle}>
            <Container maxWidth="lg">
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.75rem' },
                      fontWeight: 900,
                      mb: 3,
                      background: 'linear-gradient(45deg, #FFFFFF 30%, #E3F2FD 90%)',
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Transform Your Learning with AI
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      mb: 4,
                      lineHeight: 1.8,
                      fontSize: { xs: '1.1rem', md: '1.3rem' }
                    }}
                  >
                    Unleash your potential with IX-Study, harnessing the power of advanced AI to enhance your learning, boost retention, and accelerate your success.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/signup')}
                      sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        py: 1.5,
                        px: 4,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          transform: 'translateY(-2px)',
                          boxShadow: 4
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Get Started Free
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Features Section */}
          <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
            <Typography
              variant="h2"
              textAlign="center"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                mb: 6
              }}
            >
              Features
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 8
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mb: 3,
                          color: 'primary.main'
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        textAlign="center"
                        sx={{ fontWeight: 'bold', mb: 2 }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        textAlign="center"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>

          {/* Meta's LLaMA 3 (70B) instruction-tuned model Declaration Section */}
          <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, textAlign: 'center' }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                fontStyle: 'italic'
              }}
            >
              Powered by advanced AI: Most of our AI features, including notes generation, flashcards, and more, leverage the advanced capabilities of state-of-the-art AI models for superior performance and accuracy.
            </Typography>
          </Container>

          {/* About Section */}
          <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  mb: 3
                }}
              >
                About IX-Study
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{
                  maxWidth: '800px',
                  mx: 'auto',
                  lineHeight: 1.8
                }}
              >
                IX-Study is your intelligent study companion, powered by advanced AI technology.
                We combine cutting-edge artificial intelligence with proven learning methodologies
                to help students and professionals master any subject efficiently and effectively.
              </Typography>
            </Box>
          </Container>

          {/* Footer Links */}
          <Box
            component="footer"
            sx={{
              py: 4,
              mt: 'auto',
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Container maxWidth="lg">
              <Stack
                direction="row"
                spacing={3}
                justifyContent="center"
                alignItems="center"
              >
                <Link
                  to="/terms"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                >
                  <Typography color="text.secondary">
                    Terms of Service
                  </Typography>
                </Link>
                <Link
                  to="/privacy"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                >
                  <Typography color="text.secondary">
                    Privacy Policy
                  </Typography>
                </Link>
                <Link
                  to="/founder"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                >
                  <Typography color="text.secondary">
                    Meet the Founder
                  </Typography>
                </Link>
                {/* LICENSE AND MORE dropdown */}
                <Box>
                  <Button
                    id="license-menu-btn"
                    aria-controls={open ? 'license-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={e => setLicenseMenuAnchor(e.currentTarget)}
                    color="inherit"
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    LICENSE AND MORE
                  </Button>
                  <Menu
                    id="license-menu"
                    anchorEl={licenseMenuAnchor}
                    open={Boolean(licenseMenuAnchor)}
                    onClose={() => setLicenseMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  >
                    <MenuItem component="a" href="/COPYRIGHT.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>COPYRIGHT.txt</MenuItem>
                    <MenuItem component="a" href="/LICENSE.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>LICENSE.txt</MenuItem>
                    <MenuItem component="a" href="/LICENSES.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>LICENSES.txt</MenuItem>
                    <MenuItem component="a" href="/README.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>README.txt</MenuItem>
                    <MenuItem component="a" href="/TRADEMARK.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>TRADEMARK.txt</MenuItem>
                    <MenuItem component="a" href="/sitemap.xml" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>sitemap.xml</MenuItem>
                  </Menu>
                </Box>
              </Stack>
            </Container>
          </Box>
        </Box>
      </Fade>
      <button onClick={() => navigate('/exam-mode')} style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>Go to Exam Mode</button>
    {/* Disclaimer Banner for non-signed-in users */}
    {!isSignedIn && (
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
        zIndex: 2000,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        py: 1.5,
        textAlign: 'center',
        boxShadow: 3
      }}>
        <Typography variant="body2" color="text.secondary">
          By using this site, you agree to our{' '}
          <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</Link>{' '}and{' '}
          <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</Link>.
        </Typography>
      </Box>
    )}
  </Box>
  );
}

export default Home;